import { format } from 'date-fns'
import { FC, memo } from 'react'
import { useNavigate } from 'react-router-dom'

import s from './CampaignDetailedInfoPage.module.scss'
import styles from 'App.module.scss'

import { RuleData } from 'types/audience'
import { AdCampaignPlaces, Campaign, Playback } from 'types/campaign'
import { Creative } from 'types/creative'

import { STATUS_NAME_BY_STATUS_VALUE } from 'constants/campaignStatusConsts'
import { COUNTRY_NAME_BY_ALPHA2 } from 'constants/countryNameByAlpha'

import transformUTCToLocale from 'utils/transformUTCToLocale'

import { ArrowDownIcon } from 'assets/icons/ArrowDownIcon'

import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import Header from 'components/UI/Header'
import PageTitle from 'components/UI/PageTitle'
import { Spinner } from 'components/UI/Spinner/Spinner'
import CustomTooltip from 'components/UI/Tooltip'

interface Props {
  campaign: Campaign | null
  rules: { data: RuleData[] } | null
  loading: boolean
}
export const CampaignDetailedInfoPage: FC<Props> = memo(({ campaign, rules, loading }: Props) => {
  const navigate = useNavigate()

  return (
    <div className={s.DetailedInfo}>
      <Header className={s.header}>
        <PageTitle title="Campaign detailed info" className={s.title}>
          <CustomButton variant="secondary" size="40" onClick={() => navigate(-1)}>
            <ArrowDownIcon className={s.goBackIcon} />
          </CustomButton>
        </PageTitle>
      </Header>
      <main>
        {loading ? (
          <div className={styles.loaderWrapper}>
            <Spinner />
          </div>
        ) : (
          <>
            <h2>Main Campaign info</h2>
            <div className={`${s.campaignMainInfo} ${s.card}`}>
              <div className={s.campaignBasicInfo}>
                <p> Campaign name: {campaign?.name} </p>
                <p> Campaign ID: {campaign?.id} </p>
                <p>
                  Created at:{' '}
                  {campaign?.created_at && format(transformUTCToLocale(campaign?.created_at), 'MMM dd yyyy HH:mm')}
                </p>
                <p>
                  Starts at:{' '}
                  {campaign?.start_datetime &&
                    format(transformUTCToLocale(campaign?.start_datetime), 'MMM dd yyyy HH:mm')}
                </p>
                <p>
                  Finishs at:{' '}
                  {campaign?.start_datetime &&
                    format(transformUTCToLocale(campaign?.finish_datetime), 'MMM dd yyyy HH:mm')}
                </p>
                <p> Status: {campaign?.status && STATUS_NAME_BY_STATUS_VALUE[campaign.status]}</p>
              </div>
            </div>
            <h2>Audience info</h2>
            <div className={`${s.adSpacesInfo}`}>
              <div className={s.card}>
                <p>ID: {campaign?.audience?.id}</p>
                <p>Name: {campaign?.audience?.name}</p>
                <p>Rules count: {campaign?.audience?.seg_rules?.length}</p>
              </div>
            </div>
            <h2> Rules </h2>
            <div className={`${s.adSpacesInfo}`}>
              {campaign?.audience?.seg_rules?.map((rule) => (
                <div className={s.card} key={rule?.id}>
                  <p>Priority: {rule?.priority}</p>
                  <p>
                    Rule type:{' '}
                    {rules?.data?.length &&
                      (rule?.rule_num === 0 ? 'Default' : rules.data[rule?.rule_num - 1].rule.rule_name)}
                  </p>
                  {(!!rule?.rule_value || rule?.rule_value === 0) && <p>Rule value: {rule.rule_value}</p>}
                  {rule?.token_contract?.length ? (
                    <p className={s.tokenP}>Token address: {rule?.token_contract}</p>
                  ) : null}
                  {!!rule?.rule_geo.length && (
                    <>
                      <p>Geo{rule?.is_geo_excluded ? ' excluded' : ''}:</p>
                      <div className={s.geoWrapper}>
                        {rule?.rule_geo.map((country) => (
                          <CustomTooltip title={(COUNTRY_NAME_BY_ALPHA2 as any)[country]} key={country}>
                            <div className={s.geoCard}>{country}</div>
                          </CustomTooltip>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <h2>Selected Creatives</h2>
            <div className={s.adSpacesInfo}>
              {campaign?.audience?.seg_rules
                ?.map((rule) => rule?.seg_rule_creatives)
                ?.reduce((k, v) => [...k, ...v])
                ?.map((creative: { creative: Creative }, index) => {
                  return (
                    <div key={index} className={s.card}>
                      <p>Name: {creative.creative.name}</p>
                      <p>ID: {creative.creative.id}</p>
                    </div>
                  )
                })}
            </div>
            <h2>Selected Ad Spaces</h2>
            <div className={s.adSpacesInfo}>
              {campaign?.ad_campaign_places?.map((place: AdCampaignPlaces) => {
                return (
                  <div key={place.ad_spot.name} className={s.card}>
                    <p>Name: {place.ad_spot.name} </p>
                    <p>ID: {place.ad_spot.id} </p>
                    <p>Approved: {place.approved ? 'yes' : 'no'} </p>
                    {place.approved ? null : <p>Reason: {place.reason}</p>}
                  </div>
                )
              })}
            </div>
            <h2>Playbacks</h2>
            <div className={s.playbacksInfo}>
              {campaign?.playbacks?.map((playback: Playback) => {
                return (
                  <div key={playback?.id} className={s.card}>
                    <p>Ad Space ID: {playback?.ad_spot_id} </p>
                    <p>
                      From:{' '}
                      {playback?.from_time && format(transformUTCToLocale(playback?.from_time), 'MMM dd yyyy HH:mm:ss')}
                    </p>
                    <p>
                      To: {playback?.to_time && format(transformUTCToLocale(playback?.to_time), 'MMM dd yyyy HH:mm:ss')}
                    </p>
                    {playback?.created_at && (
                      <p>Created at: {format(transformUTCToLocale(playback?.created_at), 'MMM dd yyyy HH:mm')}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </main>
    </div>
  )
})
